import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ProgressRing from '../../progress-dashboard/components/ProgressRing';

const AdvancementReadinessCard = ({ data, onGenerateScenarios, loading = false }) => {
  const readinessScore = data?.readinessScore || 0;
  const promotion = data?.timingRecommendation?.promotion;
  const salaryIncrease = data?.timingRecommendation?.salaryIncrease;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-success';
    if (confidence >= 0.6) return 'text-warning';
    return 'text-muted-foreground';
  };

  if (loading || !data) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="TrendingUp" size={16} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Advancement Readiness</h3>
        </div>
        
        <div className="space-y-4">
          <div className="animate-pulse">
            <div className="h-32 bg-muted rounded-lg mb-4" />
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
          <Icon name="TrendingUp" size={16} className="text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Advancement Readiness</h3>
      </div>

      {/* Readiness Score */}
      <div className="flex items-center justify-center mb-6">
        <div className="text-center">
          <ProgressRing
            percentage={readinessScore}
            title={`${readinessScore}%`}
            subtitle={getScoreLabel(readinessScore)}
            size="md"
            className="mb-2"
          />
          <p className={`text-sm font-medium ${getScoreColor(readinessScore)}`}>
            {getScoreLabel(readinessScore)} Readiness
          </p>
        </div>
      </div>

      {/* Timing Recommendations */}
      <div className="space-y-4 mb-6">
        {/* Promotion Recommendation */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Icon name="ArrowUp" size={16} className="text-primary" />
              <span className="font-medium text-foreground">Promotion</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`text-sm ${getConfidenceColor(promotion?.confidence || 0)}`}>
                {Math.round((promotion?.confidence || 0) * 100)}% confidence
              </span>
              {promotion?.recommended ? (
                <Icon name="CheckCircle" size={16} className="text-success" />
              ) : (
                <Icon name="Clock" size={16} className="text-warning" />
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            <strong>Timeframe:</strong> {promotion?.timeframe || 'Not available'}
          </p>
          <p className="text-sm text-muted-foreground">
            {promotion?.reasoning || 'Analyzing your promotion readiness...'}
          </p>
        </div>

        {/* Salary Increase Recommendation */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Icon name="DollarSign" size={16} className="text-primary" />
              <span className="font-medium text-foreground">Salary Increase</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`text-sm ${getConfidenceColor(salaryIncrease?.confidence || 0)}`}>
                {Math.round((salaryIncrease?.confidence || 0) * 100)}% confidence
              </span>
              {salaryIncrease?.recommended ? (
                <Icon name="CheckCircle" size={16} className="text-success" />
              ) : (
                <Icon name="Clock" size={16} className="text-warning" />
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            <strong>Timeframe:</strong> {salaryIncrease?.timeframe || 'Not available'}
          </p>
          {salaryIncrease?.suggestedIncrease && (
            <p className="text-sm text-muted-foreground mb-2">
              <strong>Suggested Increase:</strong> {salaryIncrease?.suggestedIncrease}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            {salaryIncrease?.reasoning || 'Analyzing your salary advancement potential...'}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          variant="default"
          size="sm"
          fullWidth
          iconName="MessageSquare"
          iconPosition="left"
          onClick={() => onGenerateScenarios?.('promotion')}
          disabled={loading}
        >
          Get Promotion Strategy
        </Button>
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName="DollarSign"
          iconPosition="left"
          onClick={() => onGenerateScenarios?.('raise')}
          disabled={loading}
        >
          Get Salary Strategy
        </Button>
      </div>
    </div>
  );
};

export default AdvancementReadinessCard;